import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Added motion import

const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', jobTitle: '',
    phone: '', company: '', country: '', message: '', challenges: []
  });

  const challengesOptions = [
    "Cloud Security", "Incident Response", "Managed Detection and Response",
    "Risk and Vulnerability Management", "Security Awareness Training",
    "Regulatory Compliance", "Endpoint Security"
  ];

  const handleCheckbox = (option) => {
    setFormData(prev => ({
      ...prev,
      challenges: prev.challenges.includes(option)
        ? prev.challenges.filter(item => item !== option)
        : [...prev.challenges, option]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/submit-lead/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        // Updated: Using modal instead of alert
        setShowSuccess(true); 
        setFormData({
          firstName: '', lastName: '', email: '', jobTitle: '',
          phone: '', company: '', country: '', message: '', challenges: []
        });
      } else {
        alert(`Error: ${data.error || "Submission failed"}`);
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert("Network error. Please check if the backend is running.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="w-full py-20 px-6 "> 
        <div className="w-full lg:max-w-[60%] xl:max-w-[50%] mx-auto flex flex-col lg:flex-row  rounded-2xl overflow-hidden shadow-2xl min-h-[600px]">
          
          {/* Left Column */}
          <div className="lg:w-2/5 bg-gradient-to-br from-[#0b632fff] to-[#052e1f] text-white p-10 flex flex-col justify-center">
            <h2 className="text-3xl font-bold mb-6 leading-tight">
              Ready to <br />
              <span className="text-blue-300 italic font-medium">get started?</span>
            </h2>
            <p className="text-slate-300 text-sm mb-10 leading-relaxed">
              Reach out to schedule an introductory call with one of our team members.
            </p>
            {/* Standard contact icons can go here */}
          </div>

          {/* Right Column: Form */}
          <div className="lg:w-3/5 p-10 bg-white">
            <form onSubmit={handleSubmit} className="text-black grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <Input label="Email Address" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              <Input label="First Name" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} />
              <Input label="Last Name" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} />
              <Input label="Job Title" value={formData.jobTitle} onChange={e => setFormData({...formData, jobTitle: e.target.value})} />
              <Input label="Phone Number" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
              <Input label="Company Name" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} />
              
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 block">Select Country</label>
                <select 
                  value={formData.country}
                  className="w-full border-b border-slate-200 py-2 focus:border-blue-500 outline-none text-sm bg-transparent"
                  onChange={e => setFormData({...formData, country: e.target.value})}
                  required
                >
                  <option value="">Choose...</option>
                  <option value="US">United States</option>
                  <option value="UK">United Kingdom</option>
                  <option value="BD">Bangladesh</option>
                </select>
              </div>

              <div className="md:col-span-2 mt-4">
                <p className="text-sm font-bold text-slate-800 mb-4">What challenges are you looking to solve?</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {challengesOptions.map(opt => (
                    <label key={opt} className="flex items-center gap-3 text-[12px] text-slate-600 cursor-pointer hover:text-blue-600 transition-colors">
                      <input 
                        type="checkbox" 
                        checked={formData.challenges.includes(opt)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" 
                        onChange={() => handleCheckbox(opt)} 
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 mt-8">
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold rounded-md shadow-xl transition-all uppercase tracking-[0.2em] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-95'}`}
                >
                  {isSubmitting ? 'Sending...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Render the modal outside the main section flow */}
      <SuccessModal isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
    </>
  );
};

/* Success Modal Component with AnimatePresence for smooth entry/exit */
const SuccessModal = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl border border-slate-100"
          >
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-green-600 text-2xl font-bold">✓</span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Success!</h3>
            <p className="text-slate-500 text-sm mb-6">Your request has been submitted. Our team will reach out shortly.</p>
            <button 
              onClick={onClose}
              className="w-full py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-colors uppercase text-[10px] tracking-widest"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

/* Reusable Input Component */
const Input = ({ label, type = "text", onChange, value }) => (
  <div className="flex flex-col">
    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</label>
    <input 
      type={type} 
      value={value}
      onChange={onChange}
      className="border-b border-slate-200 py-2 focus:border-blue-500 outline-none text-sm transition-all bg-transparent text-slate-900"
      required 
    />
  </div>
);

export default ContactSection;